import React, { useState, useRef, useEffect } from 'react'
import { studioApi } from '../api/client'
import type { ChatOp, Segment, Transcript } from '../types'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  ops?: ChatOp[]
  highlights?: Array<{
    start_time: number
    end_time: number
    title: string
    description: string
    importance_score: number
    tags: string[]
  }>
  segments?: Segment[]
  timestamp: Date
}

interface ChatPanelProps {
  sessionId?: string
  transcript: Transcript | null
  videoDuration: number
  segments: Segment[]
  // Audio already on the timeline. The assistant cannot remove what it cannot
  // see — without this it names sounds from conversation memory and has no ids.
  sounds?: Array<{ id: string; kind: 'voice' | 'music' | 'sfx'; name: string; start: number; duration: number }>
  onAddSegments: (segments: Segment[]) => void
  onApplyOps: (ops: ChatOp[]) => void
  onSeek: (time: number) => void
  seedMessage?: string | null
  onSeedConsumed?: () => void
}

const SUGGESTION_CHIPS = [
  '🎯 Pull the 3 best moments',
  '✂️ Cut the intro chatter',
  '🔍 Tighten clip 1 to one sentence either side',
  '😂 Find funny moments',
  '💡 Key insights & takeaways',
  '🔥 Most quotable soundbites',
]

const OP_BADGE: Record<ChatOp['op'], { label: string; className: string }> = {
  add: { label: 'ADD', className: 'bg-green-600/30 text-green-300' },
  adjust: { label: 'ADJUST', className: 'bg-amber-600/30 text-amber-300' },
  remove: { label: 'REMOVE', className: 'bg-red-600/30 text-red-300' },
  // Sky, not green/amber/red: a sound placement is not a clip edit, and the
  // badge colour is the fastest way to see that at a glance in the op list.
  sfx: { label: 'SOUND', className: 'bg-sky-600/30 text-sky-300' },
  // Red like `remove`, because it deletes — but SOUND− so it is never mistaken
  // for dropping a clip. The two are one keystroke apart in intent and worlds
  // apart in consequence.
  remove_sound: { label: 'SOUND −', className: 'bg-red-600/30 text-red-300' },
}

export function ChatPanel({
  sessionId,
  transcript,
  videoDuration,
  segments,
  sounds,
  onAddSegments,
  onApplyOps,
  onSeek,
  seedMessage,
  onSeedConsumed,
}: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // Applied state per op card, keyed `${messageId}:${opIndex}`
  const [appliedOps, setAppliedOps] = useState<Record<string, boolean>>({})
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return
    if (!transcript && !sessionId) {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Please analyze a video first so I have a transcript to work with.',
        timestamp: new Date(),
      }])
      return
    }

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setIsLoading(true)

    try {
      // Build conversation history for context
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-6)
        .map(m => ({ role: m.role, content: m.content }))

      // Always send transcript directly — backend cannot load sessions
      const transcriptData = transcript ? {
        segments: transcript.sentences.map(s => ({
          start: s.start,
          end: s.end,
          text: s.text,
        }))
      } : undefined

      // Chat v2: sentence-indexed transcript + the CURRENT timeline segments
      const sentences = transcript
        ? transcript.sentences.map(s => ({ start: s.start, end: s.end, text: s.text }))
        : undefined
      const currentSegments = segments.map(s => ({
        id: s.id,
        start: s.start,
        end: s.end,
        title: (s.text || '').slice(0, 120),
      }))

      const response = await studioApi.chatWithVideo({
        session_id: sessionId,
        message: text.trim(),
        history,
        transcript: transcriptData,
        sentences,
        segments: currentSegments,
        sounds: sounds || [],
        video_duration: videoDuration,
        settings: {
          max_clips: 6,
          min_duration: 15,
          max_duration: 90,
        },
      })

      const ops = Array.isArray(response.ops) && response.ops.length > 0 ? response.ops : undefined
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response.reply || response.message || 'Here are the clips I found:',
        ops,
        // Legacy rendering only when the backend didn't return v2 ops
        highlights: ops ? undefined : response.highlights,
        segments: ops ? undefined : response.segments,
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, assistantMsg])
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error || error?.message || 'Something went wrong'
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, I couldn't process that: ${errorMsg}`,
        timestamp: new Date(),
      }])
    } finally {
      setIsLoading(false)
      inputRef.current?.focus()
    }
  }

  useEffect(() => {
    if (seedMessage && seedMessage.trim()) {
      sendMessage(seedMessage)
      onSeedConsumed?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const handleAddToTimeline = (msg: ChatMessage) => {
    if (msg.segments && msg.segments.length > 0) {
      onAddSegments(msg.segments)
    }
  }

  const opKey = (msgId: string, index: number) => `${msgId}:${index}`

  const handleApplyOp = (msgId: string, index: number, op: ChatOp) => {
    if (appliedOps[opKey(msgId, index)]) return
    onApplyOps([op])
    setAppliedOps(prev => ({ ...prev, [opKey(msgId, index)]: true }))
  }

  const handleApplyAll = (msg: ChatMessage) => {
    if (!msg.ops) return
    const pending = msg.ops.filter((_, i) => !appliedOps[opKey(msg.id, i)])
    if (pending.length === 0) return
    onApplyOps(pending)
    setAppliedOps(prev => {
      const next = { ...prev }
      msg.ops!.forEach((_, i) => { next[opKey(msg.id, i)] = true })
      return next
    })
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const opTitle = (op: ChatOp) => {
    if (op.op === 'add') return op.title || 'New clip'
    const target = op.id ? segments.find(s => s.id === op.id) : undefined
    return op.title || (target?.text || '').slice(0, 60) || op.id || 'Segment'
  }

  const renderOpCard = (msg: ChatMessage, op: ChatOp, index: number) => {
    const applied = !!appliedOps[opKey(msg.id, index)]
    const badge = OP_BADGE[op.op]
    const target = op.id ? segments.find(s => s.id === op.id) : undefined
    const hasRange = op.start !== undefined && op.end !== undefined

    return (
      <div
        key={index}
        className="bg-gray-800/60 rounded-lg p-2 cursor-pointer hover:bg-gray-800 transition-colors border border-gray-600/40"
        onClick={() => { if (op.start !== undefined) onSeek(op.start) }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 font-medium ${badge.className}`}>
              {badge.label}
            </span>
            <span className="text-xs font-medium text-white truncate">{opTitle(op)}</span>
          </div>
          {op.op !== 'remove' && hasRange && (
            <span className="text-[10px] text-indigo-400 shrink-0 font-mono">
              {op.op === 'adjust' && target ? (
                <>{formatTime(target.start)}—{formatTime(target.end)} → {formatTime(op.start!)}—{formatTime(op.end!)}</>
              ) : (
                <>{formatTime(op.start!)} — {formatTime(op.end!)}</>
              )}
            </span>
          )}
        </div>

        {(op.description || op.reason) && (
          <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{op.description || op.reason}</p>
        )}

        <div className="flex items-center gap-1.5 mt-1">
          {op.op === 'add' && op.score !== undefined && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-600/30 text-indigo-300">
              Score: {op.score}
            </span>
          )}
          {op.start_sentence !== undefined && op.end_sentence !== undefined && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-600/50 text-gray-300">
              Sentences {op.start_sentence}–{op.end_sentence}
            </span>
          )}
          {op.op !== 'add' && op.id && !target && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-600/50 text-gray-400" title={op.id}>
              segment gone
            </span>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation()
              handleApplyOp(msg.id, index, op)
            }}
            disabled={applied}
            className={`ml-auto text-[11px] px-2 py-0.5 rounded-md transition-colors border ${
              applied
                ? 'bg-green-600/20 text-green-400 border-green-600/30 cursor-default'
                : 'bg-indigo-600/20 text-indigo-300 border-indigo-600/30 hover:bg-indigo-600/40'
            }`}
          >
            {applied ? '✓ Applied' : 'Apply'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-4">
            <div className="text-2xl mb-2">💬</div>
            <h3 className="text-sm font-medium text-gray-300 mb-1">Chat with your video</h3>
            <p className="text-xs text-gray-500 mb-4">
              Ask me to find moments, or to add, tighten, and remove clips on your timeline.
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => sendMessage(chip.replace(/^[^\s]+\s/, ''))}
                  className="text-xs px-2.5 py-1.5 rounded-full bg-gray-700/50 text-gray-300
                    hover:bg-indigo-600/30 hover:text-indigo-300 transition-colors border border-gray-600/50"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${
              msg.role === 'user'
                ? 'bg-indigo-600 rounded-2xl rounded-br-md px-3 py-2'
                : 'bg-gray-700/80 rounded-2xl rounded-bl-md px-3 py-2'
            }`}>
              <p className="text-sm text-white whitespace-pre-wrap">{msg.content}</p>

              {/* Op cards (chat v2) */}
              {msg.ops && msg.ops.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {msg.ops.map((op, i) => renderOpCard(msg, op, i))}

                  {msg.ops.length > 1 && (
                    <button
                      onClick={() => handleApplyAll(msg)}
                      disabled={msg.ops.every((_, i) => appliedOps[opKey(msg.id, i)])}
                      className="w-full mt-1 text-xs py-1.5 rounded-lg bg-green-600/20 text-green-400
                        hover:bg-green-600/40 transition-colors border border-green-600/30
                        disabled:opacity-50 disabled:cursor-default disabled:hover:bg-green-600/20"
                    >
                      {msg.ops.every((_, i) => appliedOps[opKey(msg.id, i)])
                        ? '✓ All applied'
                        : `✅ Apply all ${msg.ops.length} changes`}
                    </button>
                  )}
                </div>
              )}

              {/* Highlight cards (legacy responses) */}
              {msg.highlights && msg.highlights.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {msg.highlights.map((h, i) => (
                    <div
                      key={i}
                      className="bg-gray-800/60 rounded-lg p-2 cursor-pointer hover:bg-gray-800 transition-colors border border-gray-600/40"
                      onClick={() => onSeek(h.start_time)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-white truncate">{h.title}</span>
                        <span className="text-[10px] text-indigo-400 shrink-0 font-mono">
                          {formatTime(h.start_time)} — {formatTime(h.end_time)}
                        </span>
                      </div>
                      {h.description && (
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{h.description}</p>
                      )}
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-600/30 text-indigo-300">
                          Score: {h.importance_score}
                        </span>
                        {h.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-600/50 text-gray-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Add all to timeline button */}
                  {msg.segments && msg.segments.length > 0 && (
                    <button
                      onClick={() => handleAddToTimeline(msg)}
                      className="w-full mt-1 text-xs py-1.5 rounded-lg bg-green-600/20 text-green-400
                        hover:bg-green-600/40 transition-colors border border-green-600/30"
                    >
                      ✅ Add {msg.segments.length} clips to timeline
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-700/80 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick suggestions (compact row once conversation started) */}
      {messages.length > 0 && (
        <div className="shrink-0 px-2 pb-1 flex gap-1.5 overflow-x-auto">
          {SUGGESTION_CHIPS.slice(0, 3).map((chip) => (
            <button
              key={chip}
              onClick={() => sendMessage(chip.replace(/^[^\s]+\s/, ''))}
              disabled={isLoading}
              className="text-[11px] px-2 py-1 rounded-full bg-gray-700/50 text-gray-300 whitespace-nowrap
                hover:bg-indigo-600/30 hover:text-indigo-300 transition-colors border border-gray-600/50
                disabled:opacity-50"
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* NO COMPOSER HERE - ON PURPOSE.
          There used to be a second input right here, which made the Studio look like
          it had two chats. It never was two: the bottom command bar just seeds this
          panel (App.runCommand -> setChatSeed -> the seedMessage effect above), so
          both boxes always drove the same conversation.
          One composer, under the video, where the eye already is. This panel is the
          transcript. A pointer, not a blank gap, so nobody hunts for where to type. */}
      <div className="shrink-0 border-t border-gray-700 px-3 py-2 text-center">
        <span className="text-xs text-gray-500">
          Type in the command bar below the video &darr;
        </span>
      </div>
    </div>
  )
}
